import {
  INCIDENT_INFORMATION_CONSTANTS,
  GENERAL_FORM_CONSTANTS,
  PHYSICIAN_INFORMATION_CONSTANTS,
  FILE_ATTACHMENTS_CONSTANTS,
  SECONDARY_INJURED_BODY_PART_REQUIRED_ERROR,
  SECONDARY_NATURE_OF_INJURY_REQUIRED_ERROR,
} from "src/forms/constants";
import { CUSTOM_VALIDATION_TYPES, FIRST, FormOverride, SECOND } from "../../types";
import {
  HELMSMAN_BODY_PART_OPTIONS,
  HELMSMAN_BODY_SIDE_OPTIONS,
  HELMSMAN_CAUSE_OF_INJURY_OPTIONS,
  HELMSMAN_CONCERNS_OPTIONS,
  HELMSMAN_NATURE_OF_INJURY,
  USA_STATES,
} from "src/forms/options";
import {
  COMBINATION_NOT_SUPPORTED_ERROR,
  DIFFERENT_COMBINED_VALUE_PRIMARY_ERROR,
  DIFFERENT_COMBINED_VALUE_SECONDARY_ERROR,
  INJURED_BODY_PART_NATURE_OF_INJURY_VALID_COMBINATIONS,
} from "../validations/constants";

export const helmsmanFormOverride: FormOverride = {
  state: {
    [INCIDENT_INFORMATION_CONSTANTS.SOURCE_OF_INJURY]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.INITIAL_MEDICAL_TREATMENT]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.NATURE_OF_INJURY]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.BODY_PART_INFORMATION_0]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.BODY_PART_INFORMATION_1]: {
      hidden: true,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_BODY_PART]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_INJURED_BODY_PART]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_SIDE]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_INJURED_SIDE]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.ADD_BODY_PART_NAME]: {
      hidden: false,
      ignoreInFinalForm: true,
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_NATURE_OF_INJURY]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.ACCIDENT_TYPE]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.EMS_CALLED]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.EMS_ASSESSMENT]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    [INCIDENT_INFORMATION_CONSTANTS.REFERRAL_REASONS]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    [INCIDENT_INFORMATION_CONSTANTS.REFERRAL_REASONS_OTHER_DESCRIPTION]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    [FILE_ATTACHMENTS_CONSTANTS.AVAILABLE_MEDICAL_DOCUMENTS_ATTACHMENTS]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    [FILE_ATTACHMENTS_CONSTANTS.IRF_OVERRIDE]: {
      hidden: false,
    },
  },
  //TODO: Setup the config for the Helmsman field when we add the option lists
  config: {
    [INCIDENT_INFORMATION_CONSTANTS.NATURE_OF_INJURY]: {
      options: HELMSMAN_NATURE_OF_INJURY,
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_NATURE_OF_INJURY]: {
      options: HELMSMAN_NATURE_OF_INJURY,
    },
    [INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_BODY_PART]: {
      options: HELMSMAN_BODY_PART_OPTIONS,
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_INJURED_BODY_PART]: {
      options: HELMSMAN_BODY_PART_OPTIONS,
    },
    [INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_SIDE]: {
      options: HELMSMAN_BODY_SIDE_OPTIONS,
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_INJURED_SIDE]: {
      options: HELMSMAN_BODY_SIDE_OPTIONS,
    },
    [INCIDENT_INFORMATION_CONSTANTS.CAUSE_OF_INJURY]: {
      label: "Cause of Injury",
      options: HELMSMAN_CAUSE_OF_INJURY_OPTIONS,
    },
    [INCIDENT_INFORMATION_CONSTANTS.QUESTIONABLE_CLAIM_REASONS]: {
      options: HELMSMAN_CONCERNS_OPTIONS,
    },
    [INCIDENT_INFORMATION_CONSTANTS.BODY_PART_INFORMATION_0]: {
      contained: true,
    },
    [INCIDENT_INFORMATION_CONSTANTS.BODY_PART_INFORMATION_1]: {
      contained: true,
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.HOSPITAL_INFORMATION_STATE]: {
      options: USA_STATES,
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.PHYSICIAN_INFORMATION_0_STATE]: {
      options: USA_STATES,
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.PHYSICIAN_INFORMATION_1_STATE]: {
      options: USA_STATES,
    },
  },
  validation: {
    [INCIDENT_INFORMATION_CONSTANTS.SOURCE_OF_INJURY]: {
      required: { value: true, message: "Source of Injury is required." },
      maxCharacters: {
        value: 15,
        message: "Source of Injury cannot be more than 15 characters long.",
      },
      minCharacters: {
        value: 2,
        message: "Source of Injury cannot be less than 2 characters long.",
      },
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.INITIAL_MEDICAL_TREATMENT]: {
      required: { value: true },
    },
    [INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_BODY_PART]: {
      required: { value: true },
      [CUSTOM_VALIDATION_TYPES.VALID_COMBINED_VALUE]: {
        value: true,
        message: COMBINATION_NOT_SUPPORTED_ERROR,
        joinWith: {
          value: INCIDENT_INFORMATION_CONSTANTS.NATURE_OF_INJURY,
          message: COMBINATION_NOT_SUPPORTED_ERROR,
        },
        acceptedValuesList: INJURED_BODY_PART_NATURE_OF_INJURY_VALID_COMBINATIONS,
        order: FIRST,
      },
      [CUSTOM_VALIDATION_TYPES.DIFFERENT_COMBINED_VALUE]: {
        value: true,
        message: DIFFERENT_COMBINED_VALUE_PRIMARY_ERROR,
        joinWith: {
          value: INCIDENT_INFORMATION_CONSTANTS.NATURE_OF_INJURY,
          message: DIFFERENT_COMBINED_VALUE_PRIMARY_ERROR,
        },
        compareWith: {
          first: {
            value: INCIDENT_INFORMATION_CONSTANTS.SECONDARY_INJURED_BODY_PART,
            message: DIFFERENT_COMBINED_VALUE_SECONDARY_ERROR,
          },
          second: {
            value: INCIDENT_INFORMATION_CONSTANTS.SECONDARY_NATURE_OF_INJURY,
            message: DIFFERENT_COMBINED_VALUE_SECONDARY_ERROR,
          },
        },
        order: FIRST,
      },
    },
    [INCIDENT_INFORMATION_CONSTANTS.NATURE_OF_INJURY]: {
      required: { value: true },
      [CUSTOM_VALIDATION_TYPES.VALID_COMBINED_VALUE]: {
        value: true,
        message: COMBINATION_NOT_SUPPORTED_ERROR,
        joinWith: {
          value: INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_BODY_PART,
          message: COMBINATION_NOT_SUPPORTED_ERROR,
        },
        acceptedValuesList: INJURED_BODY_PART_NATURE_OF_INJURY_VALID_COMBINATIONS,
        order: SECOND,
      },
      [CUSTOM_VALIDATION_TYPES.DIFFERENT_COMBINED_VALUE]: {
        value: true,
        message: DIFFERENT_COMBINED_VALUE_PRIMARY_ERROR,
        joinWith: {
          value: INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_BODY_PART,
          message: DIFFERENT_COMBINED_VALUE_PRIMARY_ERROR,
        },
        compareWith: {
          first: {
            value: INCIDENT_INFORMATION_CONSTANTS.SECONDARY_INJURED_BODY_PART,
            message: DIFFERENT_COMBINED_VALUE_SECONDARY_ERROR,
          },
          second: {
            value: INCIDENT_INFORMATION_CONSTANTS.SECONDARY_NATURE_OF_INJURY,
            message: DIFFERENT_COMBINED_VALUE_SECONDARY_ERROR,
          },
        },
        order: SECOND,
      },
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_INJURED_BODY_PART]: {
      required: {
        value: true,
        message: SECONDARY_INJURED_BODY_PART_REQUIRED_ERROR,
      },
      [CUSTOM_VALIDATION_TYPES.VALID_COMBINED_VALUE]: {
        value: true,
        message: COMBINATION_NOT_SUPPORTED_ERROR,
        joinWith: {
          value: INCIDENT_INFORMATION_CONSTANTS.SECONDARY_NATURE_OF_INJURY,
          message: COMBINATION_NOT_SUPPORTED_ERROR,
        },
        acceptedValuesList: INJURED_BODY_PART_NATURE_OF_INJURY_VALID_COMBINATIONS,
        order: FIRST,
      },
      [CUSTOM_VALIDATION_TYPES.DIFFERENT_COMBINED_VALUE]: {
        value: true,
        message: DIFFERENT_COMBINED_VALUE_SECONDARY_ERROR,
        joinWith: {
          value: INCIDENT_INFORMATION_CONSTANTS.SECONDARY_NATURE_OF_INJURY,
          message: DIFFERENT_COMBINED_VALUE_SECONDARY_ERROR,
        },
        compareWith: {
          first: {
            value: INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_BODY_PART,
            message: DIFFERENT_COMBINED_VALUE_PRIMARY_ERROR,
          },
          second: {
            value: INCIDENT_INFORMATION_CONSTANTS.NATURE_OF_INJURY,
            message: DIFFERENT_COMBINED_VALUE_PRIMARY_ERROR,
          },
        },
        order: FIRST,
      },
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_NATURE_OF_INJURY]: {
      required: {
        value: true,
        message: SECONDARY_NATURE_OF_INJURY_REQUIRED_ERROR,
      },
      [CUSTOM_VALIDATION_TYPES.VALID_COMBINED_VALUE]: {
        value: true,
        message: COMBINATION_NOT_SUPPORTED_ERROR,
        joinWith: {
          value: INCIDENT_INFORMATION_CONSTANTS.SECONDARY_INJURED_BODY_PART,
          message: COMBINATION_NOT_SUPPORTED_ERROR,
        },
        acceptedValuesList: INJURED_BODY_PART_NATURE_OF_INJURY_VALID_COMBINATIONS,
        order: SECOND,
      },
      [CUSTOM_VALIDATION_TYPES.DIFFERENT_COMBINED_VALUE]: {
        value: true,
        message: DIFFERENT_COMBINED_VALUE_SECONDARY_ERROR,
        joinWith: {
          value: INCIDENT_INFORMATION_CONSTANTS.SECONDARY_INJURED_BODY_PART,
          message: DIFFERENT_COMBINED_VALUE_SECONDARY_ERROR,
        },
        compareWith: {
          first: {
            value: INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_BODY_PART,
            message: DIFFERENT_COMBINED_VALUE_PRIMARY_ERROR,
          },
          second: {
            value: INCIDENT_INFORMATION_CONSTANTS.NATURE_OF_INJURY,
            message: DIFFERENT_COMBINED_VALUE_PRIMARY_ERROR,
          },
        },
        order: SECOND,
      },
    },
    [INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_SIDE]: {
      required: { value: true },
    },
    [INCIDENT_INFORMATION_CONSTANTS.ACCIDENT_TYPE]: {
      required: { value: false },
    },
    [FILE_ATTACHMENTS_CONSTANTS.INITIAL_REPORT_FORM]: {
      required: {
        value: true,
        message: FILE_ATTACHMENTS_CONSTANTS.IRF_REQUIRED_ERROR,
      },
    },
    [FILE_ATTACHMENTS_CONSTANTS.AUTHORIZATION_FOR_INITIAL_MEDICAL_EVALUATION]: {
      required: {
        value: true,
        message:
          FILE_ATTACHMENTS_CONSTANTS.AUTHORIZATION_FOR_INITIAL_MEDICAL_EVALUATION_REQUIRED_ERROR,
      },
    },
    [FILE_ATTACHMENTS_CONSTANTS.AUSTIN_NOTES]: {
      required: {
        value: true,
        message: FILE_ATTACHMENTS_CONSTANTS.AUSTIN_NOTES_REQUIRED_ERROR,
      },
    },
    // Reset postal code validations to US defaults (overrides Canada-specific settings)
    [PHYSICIAN_INFORMATION_CONSTANTS.PHYSICIAN_INFORMATION_0_ZIPCODE]: {
      allowedCharactersRegExp: {
        value: new RegExp(GENERAL_FORM_CONSTANTS.ONLY_NUMBERS_REGEX),
        message: GENERAL_FORM_CONSTANTS.ZIP_CODE_INVALID_CHARACTERS_MESSAGE,
      },
      maxCharacters: { value: 5 },
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.PHYSICIAN_INFORMATION_1_ZIPCODE]: {
      allowedCharactersRegExp: {
        value: new RegExp(GENERAL_FORM_CONSTANTS.ONLY_NUMBERS_REGEX),
        message: GENERAL_FORM_CONSTANTS.ZIP_CODE_INVALID_CHARACTERS_MESSAGE,
      },
      maxCharacters: { value: 5 },
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.HOSPITAL_INFORMATION_ZIPCODE]: {
      allowedCharactersRegExp: {
        value: new RegExp(GENERAL_FORM_CONSTANTS.ONLY_NUMBERS_REGEX),
        message: GENERAL_FORM_CONSTANTS.ZIP_CODE_INVALID_CHARACTERS_MESSAGE,
      },
      maxCharacters: { value: 5 },
    },
  },
};
